import React, { useEffect, useState } from 'react'
import {
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core'
import { send_command } from 'components/api'
import { useTenant } from '../context/TenantContext'
import { ErrorPop } from 'components/ErrorPop'
import { getApiErrorMessage } from 'utils/getApiErrorMessage'

const TRUST_METHOD_LABELS = {
    Match: 'Match — subject must select all required categories',
    Accept: 'Accept — subject acknowledges categories',
    NoShow: 'NoShow — trust metrics not shown to subject',
}

export function TenantTrustMetricsTab() {
    const { tenantId, details } = useTenant()
    const [trustData, setTrustData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    useEffect(() => {
        setLoading(true)
        send_command('ADMIN_GET_TRUST_METRICS', { tenant_id: tenantId })
            .then(data => {
                setTrustData(data)
                setLoading(false)
            })
            .catch(ex => {
                setError(getApiErrorMessage(ex, 'Failed to load trust metrics'))
                setLoading(false)
            })
    }, [tenantId])

    const trustCategories = trustData?.trust_categories || details?.trust_categories || []
    const trustMethod = details?.tenant_details?.trust_metric_method

    return (
        <>
            <Grid container spacing={2}>

                {/* Trust method */}
                <Grid item xs={12}>
                    <div className="superadmin-card">
                        <Typography variant="subtitle1" gutterBottom>Trust metric method</Typography>
                        {trustMethod ? (
                            <>
                                <Typography variant="body1" gutterBottom>
                                    <strong>{trustMethod}</strong>
                                </Typography>
                                {TRUST_METHOD_LABELS[trustMethod] && (
                                    <Typography variant="body2" color="textSecondary">
                                        {TRUST_METHOD_LABELS[trustMethod]}
                                    </Typography>
                                )}
                            </>
                        ) : (
                            <Typography variant="body2" color="textSecondary">Not configured</Typography>
                        )}
                    </div>
                </Grid>

                {/* Trust categories */}
                <Grid item xs={12}>
                    <div className="superadmin-card">
                        <Typography variant="subtitle1" gutterBottom>Trust categories</Typography>
                        {loading && (
                            <Typography variant="body2" color="textSecondary">Loading…</Typography>
                        )}
                        {!loading && trustCategories.length > 0 && (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Category</TableCell>
                                        <TableCell>Key</TableCell>
                                        <TableCell>Census category</TableCell>
                                        <TableCell>Has destination</TableCell>
                                        <TableCell>Options</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {trustCategories.map((cat, i) => (
                                        <TableRow key={cat.key || i}>
                                            <TableCell>{cat.category}</TableCell>
                                            <TableCell>
                                                <span style={{ fontFamily: 'monospace' }}>{cat.key}</span>
                                            </TableCell>
                                            <TableCell>{cat.census_category ? 'Yes' : 'No'}</TableCell>
                                            <TableCell>{cat.has_destination ? 'Yes' : 'No'}</TableCell>
                                            <TableCell>
                                                {Array.isArray(cat.options) && cat.options.length > 0
                                                    ? cat.options.map(o => o.name || o.key).join(', ')
                                                    : '—'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                        {!loading && trustCategories.length === 0 && (
                            <Typography variant="body2" color="textSecondary">No trust categories configured</Typography>
                        )}
                    </div>
                </Grid>

                {/* Raw trust metric data */}
                {trustData && (
                    <Grid item xs={12}>
                        <div className="superadmin-card">
                            <Typography variant="subtitle1" gutterBottom>Trust metric data</Typography>
                            <pre style={{ fontSize: 12, overflow: 'auto', maxHeight: 300 }}>
                                {JSON.stringify(trustData, null, 2)}
                            </pre>
                        </div>
                    </Grid>
                )}
            </Grid>

            <ErrorPop error={error} setError={setError} timeout={6000} />
        </>
    )
}
